import {
  Box,
  Button,
  Grid,
  List,
  ListItem,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useState } from "react";
import { BankingBreakdownStyles } from "src/components/banking/styles/BankingBreakdown.styles";
import TabPanel from "src/components/common/TabPanel";
import {
  useCreateMonoovaAccountMutation,
  useGetMonoovaAccountDetailsQuery,
} from "src/store/features/api/generated";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import GenerateAccount from "src/components/banking/GenerateAccount";
import { LoadingButton } from "@mui/lab";
import DetailsRow from "src/components/banking/BankingRow";
import HighlightedInformation from "src/components/banking/HighlightedInformation";
import { useTheme } from "@mui/styles";
import { BootstrappedTab, BootstrappedTabs } from "./BootstrappedTabs";
import { trackEvent } from "src/Amplitude";

interface AusBankingProps {
  copyToClipboard: (el: any) => void;
  countryCode: string;
}

function AusBanking(props: AusBankingProps) {
  // props
  const { copyToClipboard, countryCode } = props;

  // styles
  const classes = BankingBreakdownStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [value, setValue] = React.useState(0);
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingButtonText, setLoadingButtonText] = useState("Generate PayID");

  // redux
  const { data, isSuccess, refetch } = useGetMonoovaAccountDetailsQuery();
  const [createPayId] = useCreateMonoovaAccountMutation();

  // const
  const copyMessage = `Copy Your PayID Email Address Below`;
  const selectMethod = `Select payment method`;
  const clickToGenerate = `Please click 'Generate PayID' to create a PayID`;
  // functions
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const generatePayId = async () => {
    setLoadingButton(true);
    await createPayId()
      .unwrap()
      .then((res) => {
        setLoadingButton(false);
        setLoadingButtonText("Success");
        setTimeout(() => {
          refetch();
        }, 1000);
      })
      .catch((e) => {
        console.error("Error: generatePayId");
        setLoadingButton(false);
        setLoadingButtonText("Error");
      });
  };

  return (
    <>
      {/* LOADING DATA */}
      {!isSuccess && <Skeleton variant="rectangular" width={"100%"} height={400} />}
      {isSuccess && (
        <Grid container className={classes.container} sx={{ overflow: "hidden" }}>
          <Stack direction="column" spacing={3}>
            {/* USER HAS AN ACCOUNT */}
            {/* MUST CHECK BANK ACCOUNT NUMBER TO SHOW DETAILS */}
            {data.bankAccountNumber ? (
              <>
                <HighlightedInformation
                  currency={"AUD"}
                  countryCode={countryCode}
                  showTransferTimes={false}
                  showDescription={true}
                />
                <Box mt={2}>
                  <Typography className={classes.title}>{selectMethod}</Typography>
                </Box>

                <Box mt={4}>
                  <BootstrappedTabs
                    value={value}
                    onChange={handleChange}
                    orientation={isMobile ? "vertical" : "horizontal"}>
                    <BootstrappedTab
                      label="PayID (Email)"
                      id="banking-tab-1"
                      aria-controls="banking-tabpanel-0"
                    />
                    <BootstrappedTab
                      label="Bank Transfer (BSB & Account Number)"
                      id="banking-tab-2"
                      aria-controls="banking-tabpanel-1"
                    />
                  </BootstrappedTabs>
                </Box>

                <TabPanel value={value} index={0} name={"banking"}>
                  <Stack direction="column" spacing={4} sx={{ width: "100%" }}>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: "600", margin: "0" }}>
                          {copyMessage}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          borderRadius: "10px",
                          paddingLeft: "1rem",
                          background: "#F8F9F9",
                          width: "100%",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}>
                        {data.payId && (
                          <>
                            <Typography
                              sx={{
                                fontWeight: theme.typography.fontWeightMedium,
                              }}>
                              {data.payId}
                            </Typography>
                            {window.isSecureContext && (
                              <div
                                onClick={() => {
                                  trackEvent(
                                    "Copy PayID",
                                    { accountName: data.bankAccountName, payId: data.payId },
                                    "AusBanking",
                                  );
                                  copyToClipboard({ content: data.payId });
                                }}>
                                <Button
                                  disableRipple
                                  variant="outlined"
                                  sx={{
                                    backgroundColor: theme.palette.secondary.main,
                                    color: "#fff ",
                                    fontWeight: "bold",
                                    borderColor: theme.palette.secondary.main,
                                    textTransform: "none",
                                    borderRadius: "10px",
                                    "&:hover": {
                                      borderColor: "#FEDBDB",
                                      background: "#FEDBDB",
                                      color: "#fff",
                                      opacity: "0.8",
                                      transition: "all 0.2s",
                                    },
                                  }}>
                                  Copy{" "}
                                </Button>
                              </div>
                            )}
                          </>
                        )}
                        {!data.payId && (
                          // IF USER DOES NOT HAVE PAYID - NEEDS TO GENERATE
                          <>
                            <Typography>{clickToGenerate}</Typography>
                            <LoadingButton
                              variant="outlined"
                              className={classes.icon}
                              onClick={generatePayId}
                              loading={!!loadingButton}
                              disabled={loadingButtonText === "Error"}>
                              {loadingButtonText}
                            </LoadingButton>
                          </>
                        )}
                      </Box>
                    </Stack>

                    <Stack direction="column" spacing={2} sx={{ width: "100%", marginTop: "2rem" }}>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: "600", margin: "0" }}>
                          Open your banking app to follow the steps below:
                        </Typography>
                      </Box>

                      <List sx={{ marginTop: "0 !important" }}>
                        <ListItem className={classes.listItem}>
                          <span className={classes.listItemNumber}>1</span>
                          Select "Pay Anyone"
                          <ArrowForwardIcon fontSize="small" sx={{ margin: "0 0.5rem" }} />
                          Email
                        </ListItem>
                        <ListItem className={classes.listItem}>
                          <span className={classes.listItemNumber}>2</span>Enter the amount you'd
                          like to transfer
                        </ListItem>
                        <ListItem className={classes.listItem}>
                          <span className={classes.listItemNumber}>3</span>Click "Send" to proceed
                        </ListItem>
                      </List>
                    </Stack>
                  </Stack>
                </TabPanel>

                <TabPanel value={value} index={1} name={"banking"}>
                  <Stack direction="column" spacing={2} sx={{ width: "100%" }}>
                    <Typography variant="body1" sx={{ fontWeight: "600", margin: "0" }}>
                      Please send funds to the following bank account
                    </Typography>

                    <Box>
                      <DetailsRow
                        placeholder={"BSB"}
                        content={data.bsb || 0}
                        isCopy={true}
                        copyToClipboard={copyToClipboard}
                      />
                    </Box>
                    <Box>
                      <DetailsRow
                        placeholder={"Bank Account Number"}
                        content={data.bankAccountNumber}
                        isCopy={true}
                        copyToClipboard={copyToClipboard}
                      />
                    </Box>
                    <Box>
                      <DetailsRow
                        placeholder={"Bank Account Name"}
                        content={data.bankAccountName}
                        isCopy={true}
                        copyToClipboard={copyToClipboard}
                      />
                    </Box>
                  </Stack>
                </TabPanel>
              </>
            ) : (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  textAlign: "center",
                  alignItems: "center",
                }}>
                <GenerateAccount />
              </div>
            )}
          </Stack>
        </Grid>
      )}
    </>
  );
}

export default AusBanking;
